import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useMemo } from 'react';
import cn from 'classnames';
import { ChevronDown, ChevronUp } from '../../icons';
import { useBiAction } from '../BiTracking';
import { useIsInBottomSheet } from '../BottomSheet';
import { Checkbox } from '../Checkbox';
import { useDevice } from '../Device';
import { IconButton } from '../IconButton';
import { Tooltip } from '../Tooltip';
import styles from './TableList.module.scss';
import { actionColumnDef, expandIconStyle, useTableListActions, useTableListSelector } from './TableList.utils';
import { TableListHeaderItem } from './TableListHeaderItem';
import { TableListItem } from './TableListItem';
import { TableListSortIcons } from './TableListSortIcons';
const defaultSortOrder = [
    'desc',
    'asc'
];
function getNextSort(sort, sortOrder) {
    if (!sort) {
        return sortOrder[0];
    }
    const idx = sortOrder.findIndex((so)=>so === sort.sort);
    return sortOrder[(idx + 1) % sortOrder.length];
}
const TableHeaderItem = (param)=>{
    let { item, idx, columnDef, sort, setSort, testId } = param;
    const logBiAction = useBiAction();
    const isSortable = columnDef === null || columnDef === void 0 ? void 0 : columnDef.sortable;
    const isSorted = sort && idx === sort.colIndex;
    const handleSortClick = useCallback(()=>{
        var _columnDef_bi;
        const nextSort = getNextSort(isSorted ? sort : undefined, columnDef.sortOrder || defaultSortOrder);
        if ((_columnDef_bi = columnDef.bi) === null || _columnDef_bi === void 0 ? void 0 : _columnDef_bi.name) {
            logBiAction({
                entityType: 'table_list_sort',
                entityName: columnDef.bi.name,
                actionType: 'click',
                data: _object_spread_props(_object_spread({}, columnDef.bi.data), {
                    sort_direction: nextSort || 'none',
                    test_id: testId !== null && testId !== void 0 ? testId : ''
                })
            });
        }
        setSort(nextSort && {
            colIndex: idx,
            sort: nextSort
        });
    }, [
        columnDef,
        idx,
        isSorted,
        logBiAction,
        setSort,
        sort,
        testId
    ]);
    if (!columnDef) return null;
    return /*#__PURE__*/ _jsx(TableListHeaderItem, {
        columnDef: columnDef,
        item: item,
        testId: testId && "".concat(testId, "-").concat(idx),
        children: isSortable && /*#__PURE__*/ _jsxs("button", {
            className: cn(styles.sortable, {
                [styles.sortActive]: isSorted
            }),
            "data-testid": testId && "".concat(testId, "-").concat(idx, "-sort-").concat(isSorted && sort ? sort.sort : 'none'),
            onClick: handleSortClick,
            children: [
                item.value,
                /*#__PURE__*/ _jsx(TableListSortIcons, {
                    sort: isSorted ? sort.sort : undefined
                })
            ]
        })
    }, idx);
};
export function TableListHeader(props) {
    const { isMobileDevice } = useDevice();
    const isInBottomSheet = useIsInBottomSheet();
    const { className, stickyToTop, items, size = 'medium', bgColor = 'gray', testId, expandAllTooltip = 'Expand all sub items', collapseAllTooltip = 'Collapse all sub items' } = props, rest = _object_without_properties(props, [
        "className",
        "stickyToTop",
        "items",
        "size",
        "bgColor",
        "testId",
        "expandAllTooltip",
        "collapseAllTooltip"
    ]);
    const { columnDefs, expandableRows, expandableRowsVariant = 'default', enableRowSelection, hasActionColumn, hPadding, justifyBetween } = useTableListSelector((s)=>s.config);
    const sort = useTableListSelector((s)=>s.sort);
    const numRows = useTableListSelector((s)=>s.numRows);
    const numSelectedRows = useTableListSelector((s)=>s.numSelectedRows);
    const onToggleAllSelection = useTableListSelector((s)=>s.onToggleAllSelection);
    const hasExpendedRow = useTableListSelector((s)=>Object.values(s.expandedRows).some((row)=>row));
    const { setSort, expandAllRows } = useTableListActions();
    const isExpandableDefaultVariant = expandableRows && expandableRowsVariant === 'default';
    const isExpandableClickableIconAndRowVariant = expandableRows && expandableRowsVariant === 'clickableIconAndRow';
    const isExpandableClickableIconVariant = expandableRows && (expandableRowsVariant === 'clickableIcon' || isExpandableClickableIconAndRowVariant);
    const stickyItems = useMemo(()=>items.filter((_, idx)=>{
            var _columnDefs_idx;
            return (_columnDefs_idx = columnDefs[idx]) === null || _columnDefs_idx === void 0 ? void 0 : _columnDefs_idx.sticky;
        }), [
        items,
        columnDefs
    ]);
    const stickyColumnDefs = useMemo(()=>columnDefs.filter((col)=>col.sticky), [
        columnDefs
    ]);
    const nonStickyItems = useMemo(()=>items.filter((_, idx)=>{
            var _columnDefs_idx;
            return !((_columnDefs_idx = columnDefs[idx]) === null || _columnDefs_idx === void 0 ? void 0 : _columnDefs_idx.sticky);
        }), [
        items,
        columnDefs
    ]);
    const nonStickyColumnDefs = useMemo(()=>columnDefs.filter((col)=>!col.sticky), [
        columnDefs
    ]);
    if (isMobileDevice || isInBottomSheet) {
        return null;
    }
    return /*#__PURE__*/ _jsxs("div", _object_spread_props(_object_spread({}, rest), {
        className: cn(styles.header, styles["".concat(bgColor, "Bg")], styles["".concat(hPadding, "Padding")], styles["".concat(size, "Size")], stickyToTop && styles.stickyToTop, justifyBetween && styles.justifyBetween, className),
        "data-testid": testId,
        children: [
            (enableRowSelection || isExpandableClickableIconVariant || stickyItems.length > 0) && /*#__PURE__*/ _jsxs("div", {
                className: cn(styles.stickyCheckboxWrapper, styles["".concat(hPadding, "Padding")]),
                children: [
                    (enableRowSelection || isExpandableClickableIconVariant) && /*#__PURE__*/ _jsxs("div", {
                        className: cn(styles.stickyCheckbox, styles["".concat(hPadding, "Padding")]),
                        children: [
                            enableRowSelection && /*#__PURE__*/ _jsx(Checkbox, {
                                testId: testId && "".concat(testId, "-checkbox"),
                                checked: numRows != null && numRows === numSelectedRows,
                                partiallyChecked: numSelectedRows != null && numSelectedRows > 0 && numSelectedRows !== numRows,
                                onChange: (e)=>{
                                    e.stopPropagation();
                                    if (onToggleAllSelection != null) {
                                        onToggleAllSelection(e.target.checked);
                                    }
                                },
                                // eslint-disable-next-line custom/no-hardcoded-bi-name-in-seagull -- WHY: TableList owns the inherent fixed-semantic "select all rows" action; consumers cannot rename it (rule 6a), so the name is hardcoded by design, not a missing pass-through.
                                bi: {
                                    name: 'select_all'
                                }
                            }),
                            isExpandableClickableIconVariant && /*#__PURE__*/ _jsx(Tooltip, {
                                placement: "right",
                                title: hasExpendedRow ? collapseAllTooltip : expandAllTooltip,
                                children: /*#__PURE__*/ _jsx(IconButton, {
                                    // eslint-disable-next-line custom/no-hardcoded-bi-name-in-seagull -- WHY: TableList owns the inherent fixed-semantic "expand/collapse all rows" action; consumers cannot rename it (rule 6a), so the name is hardcoded by design, not a missing pass-through.
                                    bi: {
                                        name: 'toggle_expand_all'
                                    },
                                    testId: testId && "".concat(testId, "-toggle-expand-all"),
                                    size: "small",
                                    noPadding: true,
                                    onClick: (e)=>{
                                        e.stopPropagation();
                                        expandAllRows(!hasExpendedRow);
                                    },
                                    children: hasExpendedRow ? /*#__PURE__*/ _jsx(ChevronUp, {
                                        size: 20
                                    }) : /*#__PURE__*/ _jsx(ChevronDown, {
                                        size: 20
                                    })
                                })
                            })
                        ]
                    }),
                    stickyItems.length > 0 && /*#__PURE__*/ _jsx("div", {
                        className: cn(styles.headerItemData, justifyBetween && styles.justifyBetween),
                        children: stickyItems.map((item, idx)=>{
                            var _item_testId;
                            return /*#__PURE__*/ _jsx(TableHeaderItem, {
                                item: item,
                                idx: idx,
                                columnDef: stickyColumnDefs[idx],
                                sort: sort,
                                setSort: setSort,
                                testId: (_item_testId = item.testId) !== null && _item_testId !== void 0 ? _item_testId : testId
                            }, idx);
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: cn(styles.headerItemData, justifyBetween && styles.justifyBetween),
                children: nonStickyItems.map((item, idx)=>/*#__PURE__*/ _jsx(TableHeaderItem, {
                        item: item,
                        idx: idx + stickyItems.length,
                        columnDef: nonStickyColumnDefs[idx],
                        sort: sort,
                        setSort: setSort,
                        testId: testId
                    }, idx + stickyItems.length))
            }),
            (hasActionColumn || isExpandableDefaultVariant) && /*#__PURE__*/ _jsxs("div", {
                className: cn(styles.rowActionSticky, styles["".concat(hPadding, "Padding")]),
                children: [
                    hasActionColumn && /*#__PURE__*/ _jsx(TableListItem, {
                        columnDef: actionColumnDef,
                        item: {
                            value: ''
                        },
                        testId: testId && "".concat(testId, "-action-column")
                    }),
                    isExpandableDefaultVariant && /*#__PURE__*/ _jsx("div", {
                        style: expandIconStyle
                    })
                ]
            })
        ]
    }));
}
